import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule, HttpHeaders } from '@angular/common/http';
import { SingleSelectDropdownComponent } from '../../../../UI/business-details/single-select-dropdown.component';
import { OffcanvasSheetComponent } from '../../../shared/offcanvas-sheet/offcanvas-sheet.component';
import { NumericInputDirective } from '../../../../shared/directives/numeric-input.directive';
import { parseMonetary } from '../../../../shared/services/price-parser';
import { environment } from '../../../../../../environments/environment';

interface MobileEmployee {
  employeeID: number;
  firstName: string;
  lastName: string;
  roleID?: number;
}

export interface CostItemEditData {
  itemName: string;
  category: string;
  unitCost: number;
  // Raw material specific
  inventoryId?: number;
  usageQuantity?: number;
  batchSize?: number;
  purchasePrice?: number;
  purchaseQuantity?: number;
  purchasePricePerUnit?: number;
  purchaseUnit?: string;
  usageUnit?: string;
  // Labour specific
  rateType?: string;
  itemOperationID?: number;
  employeeID?: number | null;
  monthlySalary?: number;
  hoursWorkedPm?: number;
  minutesPerJob?: number;
  minutesPerActivity?: number;
  roleDescription?: string;
  ratePerMinute?: number;
  // Fixed overhead specific
  itemId?: number | null;              // overheadCostPoolID — null = new item
  overheadCostItemID?: number | null;  // global FOH type — drives dropdown + fetch
  costPerMonth?: number;
  hoursOpenPerMonth?: number;
  minutesPerOrder?: number;
  // Variable overhead specific
  estimatedSales?: number;
  monthlyCost?: number;
  // Competitor specific
  price?: number;
  website?: string;
  isActive?: boolean;
  targetMarketSegment?: string;
  primarySalesChannel?: string;
  geographicFocus?: string;
  isTargetSellingPrice?: boolean;
  // Delete identifiers
  bomComponentID?: number;
  variableOverheadItemID?: number;
  competitorID?: number;
  // Product specific
  sellingPrice?: number;
  description?: string;
}

@Component({
  selector: 'app-cost-item-offcanvas',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule, SingleSelectDropdownComponent, OffcanvasSheetComponent, NumericInputDirective],
  templateUrl: './cost-item-offcanvas.component.html',
  styleUrls: [
    '../../../shared/offcanvas-sheet/offcanvas-sheet.shared.css',
    './cost-item-offcanvas.component.css',
  ],
})
export class CostItemOffcanvasComponent implements OnChanges {
  constructor(private http: HttpClient) {}
  @Input() visible = false;
  @Input() item: CostItemEditData | null = null;
  @Input() employees: MobileEmployee[] = [];
  @Input() fixedOverheadNames: any[] = [];
  @Input() variableOverheadItems: any[] = [];
  @Input() materialNames: any[] = [];
  @Input() accountType = 'full';
  @Input() uomList: any[] = [];

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<CostItemEditData>();

  editData: CostItemEditData = this.emptyItem();

  moneyError: string | null = null;

  readonly Math = Math;
  readonly String = String;

  get employeeOptions() {
    return this.employees.map(e => ({ value: String(e.employeeID), label: `${e.firstName} ${e.lastName}` }));
  }

  get fixedOverheadOptions() {
    return this.fixedOverheadNames.map((f: any) => ({ value: String(f.overheadCostItemID), label: f.overheadName }));
  }

  get variableOverheadOptions() {
    return this.variableOverheadItems.map((v: any) => ({ value: String(v.itemID), label: v.itemName }));
  }

  get materialOptions() {
    return this.materialNames.map((m: any) => ({ value: String(m.itemID), label: m.itemName }));
  }

  get uomOptions() {
    return this.uomList.map((u: any) => ({ value: u.uomCode, label: u.uomName }));
  }

  onPurchaseUnitChange(val: string | null): void {
    this.editData.purchaseUnit = val ?? '';
    this.recalculateDerivedFields();
  }

  onUsageUnitChange(val: string | null): void {
    this.editData.usageUnit = val ?? '';
    this.recalculateDerivedFields();
  }

  /**
   * Full-plan only: user picked an existing inventory raw material — link it
   * and auto-fill purchase price + quantity from inventory, then recalculate.
   */
  onMaterialNameChange(val: string | null): void {
    const id = val ? +val : null;
    if (!id) {
      this.editData.inventoryId = 0;
      this.recalculateDerivedFields();
      return;
    }
    const src = (this.materialNames as any[]).find((m) => m.itemID === id);
    if (!src) {
      this.recalculateDerivedFields();
      return;
    }
    this.editData.inventoryId = src.itemID;
    this.editData.itemName = src.itemName || '';
    this.editData.purchasePrice = src.purchasePrice ?? 0;
    this.editData.purchaseQuantity = src.purchaseQuantity ?? 0;
    const uom = (this.uomList as any[]).find(
      (u) => (u.uomID ?? u.uomid) === src.purchaseUOMID
    );
    if (uom) {
      this.editData.purchaseUnit = uom.uomCode;
    }
    this.recalculateDerivedFields();
  }

  onEmployeeChange(val: string | null): void {
    const empId = val ? +val : null;
    this.editData.employeeID = empId;
    if (!empId) {
      this.recalculateDerivedFields();
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const emp = (this.employees as any[]).find(e => e.employeeID === empId);

    this.http.get<any[]>(`${environment.apiUrl}/api/EmployeeSalaries/GetByEmployeeID/${empId}`, { headers })
      .subscribe({
        next: (salaries) => {
          const now = new Date();
          let chosen = salaries?.find(s => {
            const start = new Date(s.startDate);
            const end = s.endDate ? new Date(s.endDate) : null;
            return start <= now && (!end || now <= end);
          });
          if (!chosen && salaries?.length) {
            chosen = salaries.reduce((prev: any, cur: any) =>
              new Date(cur.startDate) > new Date(prev.startDate) ? cur : prev
            );
          }
          if (chosen) {
            this.editData.monthlySalary = chosen.amount ?? chosen.monthlySalary ?? 0;
            this.editData.hoursWorkedPm = chosen.hoursWorked ?? chosen.hoursWorkedPerMonth ?? chosen.hoursWorkedPm ?? 0;

            this.http.get<any>(`${environment.apiUrl}/api/PeriodTypes/GetById/${chosen.periodTypeID}`, { headers })
              .subscribe({
                next: (pt) => {
                  this.editData.rateType = pt?.description ?? 'Per Minute';
                  this.fetchRoleAndRecalculate(emp, headers);
                },
                error: () => {
                  this.editData.rateType = 'Per Minute';
                  this.fetchRoleAndRecalculate(emp, headers);
                }
              });
          } else {
            this.fetchRoleAndRecalculate(emp, headers);
          }
        },
        error: () => this.recalculateDerivedFields()
      });
  }

  private fetchRoleAndRecalculate(emp: any, headers: HttpHeaders): void {
    if (emp?.roleID) {
      this.http.get<any>(`${environment.apiUrl}/api/Role/GetById?RoleId=${emp.roleID}`, { headers })
        .subscribe({
          next: (role) => {
            this.editData.roleDescription = role?.roleName ?? role?.description ?? role?.roleDescription ?? '';
            this.recalculateDerivedFields();
          },
          error: () => this.recalculateDerivedFields()
        });
    } else {
      this.recalculateDerivedFields();
    }
  }

  onFixedOverheadChange(val: string | null): void {
    const id = val ? +val : null;
    this.editData.overheadCostItemID = id;
    if (!id) {
      this.recalculateDerivedFields();
      return;
    }
    this.triggerFixedOverheadFetch(id);
  }

  private triggerFixedOverheadFetch(id: number): void {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    this.http.get<any[]>(`${environment.apiUrl}/api/OverheadAllocationRate/GetByCostItemId/${id}`, { headers })
      .subscribe({
        next: (rates) => {
          const rate = rates?.[0];
          if (rate) {
            this.editData.costPerMonth = this.convertToMonthlyCost(rate.rateValue ?? 0, rate.periodTypeID ?? 5);
            // hoursOpenPerMonth is business-level (operating schedule) and is already
            // carried on the edit item — the allocation-rate DTO has no such field,
            // so do NOT overwrite it here (would zero out Rate/Min + Unit Cost).
          }
          this.recalculateDerivedFields();
        },
        error: () => this.recalculateDerivedFields()
      });
  }

  private convertToMonthlyCost(cost: number, periodTypeID: number): number {
    if (!cost || cost === 0) return 0;
    switch (periodTypeID) {
      case 1: return cost;         // Hourly
      case 2: return cost * 30;    // Daily
      case 3: return cost * 4;     // Weekly
      case 4: return cost * 2;     // Bi-Weekly
      case 5: return cost;         // Monthly
      case 6: return cost / 12;    // Yearly
      default: return cost;
    }
  }

  onVariableOverheadChange(val: string | null): void {
    const id = val ? +val : undefined;
    this.editData.inventoryId = id;
    if (!id) {
      this.recalculateDerivedFields();
      return;
    }

    const item = (this.variableOverheadItems as any[]).find(v => v.itemID === id);
    if (item) {
      this.editData.itemName = item.itemName || '';
      this.editData.purchasePrice = item.purchasePrice ?? 0;
      this.editData.purchaseQuantity = item.purchaseQuantity ?? 0;
      const ppu = (item.purchaseQuantity ?? 0) > 0
        ? (item.purchasePrice ?? 0) / (item.purchaseQuantity ?? 1)
        : 0;
      this.editData.purchasePricePerUnit = +ppu.toFixed(4);
    }
    this.recalculateDerivedFields();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] && this.item) {
      this.editData = { ...this.item };
      this.moneyError = null;
      // Auto-fetch allocation rate only for placeholder (new) FOH items — existing items already carry their own data
      if (this.editData.category === 'Fixed Overhead' && this.editData.overheadCostItemID && this.editData.itemId == null) {
        this.triggerFixedOverheadFetch(this.editData.overheadCostItemID);
      } else {
        this.recalculateDerivedFields();
      }
    }
  }

  onMaterialInputChange(): void {
    this.recalculateDerivedFields();
  }

  recalculateDerivedFields(): void {
    const category = this.editData.category;

    // Raw Material calculations
    if (category === 'Raw Material') {
      const price = Number(this.editData.purchasePrice ?? 0);
      const qty = Number(this.editData.purchaseQuantity ?? 0);
      const usage = Number(this.editData.usageQuantity ?? 0);
      const batch = Number(this.editData.batchSize ?? 0);

      const pricePerUnit = qty > 0 ? price / qty : 0;
      this.editData.purchasePricePerUnit = +pricePerUnit.toFixed(4);

      if (batch > 0 && usage > 0) {
        this.editData.unitCost = +((pricePerUnit * usage) / batch).toFixed(2);
      } else {
        this.editData.unitCost = +pricePerUnit.toFixed(4);
      }
      return;
    }

    // Direct/Indirect Labour calculations
    if (category === 'Direct Labor' || category === 'Indirect Labor') {
      const monthlySalary = Number(this.editData.monthlySalary ?? 0);
      const hoursWorkedPm = Number(this.editData.hoursWorkedPm ?? 0);
      const batch = Number(this.editData.batchSize ?? 0);
      const minutesPerJob = Number(this.editData.minutesPerJob ?? 0);
      const minutesPerActivity = Number(this.editData.minutesPerActivity ?? 0);

      // Calculate rate per minute
      const ratePerMinute = hoursWorkedPm > 0 ? monthlySalary / (hoursWorkedPm * 60) : 0;
      this.editData.ratePerMinute = +ratePerMinute.toFixed(4);

      // Calculate unit cost
      const minutes = category === 'Indirect Labor' ? minutesPerActivity : minutesPerJob;
      if (batch > 0 && minutes > 0) {
        this.editData.unitCost = +((ratePerMinute * minutes) / batch).toFixed(2);
      } else {
        this.editData.unitCost = 0;
      }
      return;
    }

    // Fixed Overhead calculations
    if (category === 'Fixed Overhead') {
      const costPerMonth = Number(this.editData.costPerMonth ?? 0);
      const hoursOpenPerMonth = Number(this.editData.hoursOpenPerMonth ?? 0);
      const minutesPerOrder = Number(this.editData.minutesPerOrder ?? 0);
      const batch = Number(this.editData.batchSize ?? 0);

      // Rate per minute
      const ratePerMinute = hoursOpenPerMonth > 0 ? costPerMonth / (hoursOpenPerMonth * 60) : 0;

      // Unit cost
      if (batch > 0 && minutesPerOrder > 0) {
        this.editData.unitCost = +((ratePerMinute * minutesPerOrder) / batch).toFixed(2);
      } else {
        this.editData.unitCost = 0;
      }
      return;
    }

    // Variable Overhead calculations
    if (category === 'Variable Overhead') {
      const purchasePrice = Number(this.editData.purchasePrice ?? 0);
      const purchaseQuantity = Number(this.editData.purchaseQuantity ?? 0);
      const usageQuantity = Number(this.editData.usageQuantity ?? 0);
      const batch = Number(this.editData.batchSize ?? 0);

      // Price per unit
      const pricePerUnit = purchaseQuantity > 0 ? purchasePrice / purchaseQuantity : 0;
      this.editData.purchasePricePerUnit = +pricePerUnit.toFixed(4);

      // Unit cost
      if (batch > 0 && usageQuantity > 0) {
        this.editData.unitCost = +((pricePerUnit * usageQuantity) / batch).toFixed(2);
      } else {
        this.editData.unitCost = 0;
      }
      return;
    }
  }

  // These inputs are type="text" for the decimal keypad, so unlike desktop's type="number" fields
  // they accept text such as "12,50" — which Number() turns into NaN and the API rejects.
  onSave(): void {
    const field = this.moneyField;
    const raw: unknown = field ? this.editData[field] : undefined;
    if (field && raw !== undefined && raw !== null && raw !== '') {
      const parsed = parseMonetary(raw);
      if (parsed.value === null) {
        this.moneyError = parsed.error ?? 'Enter a valid amount (e.g., 10.50)';
        return;
      }
      this.editData[field] = parsed.value;
      this.recalculateDerivedFields();
    }
    this.moneyError = null;
    this.save.emit({ ...this.editData });
    this.close.emit();
  }

  private get moneyField(): 'sellingPrice' | 'purchasePrice' | 'price' | null {
    if (this.isProduct) return 'sellingPrice';
    if (this.isMaterial) return 'purchasePrice';
    if (this.isCompetitor) return 'price';
    return null;
  }

  onClose(): void {
    this.close.emit();
  }

  get isMaterial(): boolean {
    return this.editData.category === 'Raw Material';
  }

  get isDirectLabour(): boolean {
    return this.editData.category === 'Direct Labor';
  }

  get isIndirectLabour(): boolean {
    return this.editData.category === 'Indirect Labor';
  }

  get isFixedOverhead(): boolean {
    return this.editData.category === 'Fixed Overhead';
  }

  get isVariableOverhead(): boolean {
    return this.editData.category === 'Variable Overhead';
  }

  get isCompetitor(): boolean {
    return this.editData.category === 'Competitor';
  }

  get isProduct(): boolean {
    return this.editData.category === 'Product';
  }

  private emptyItem(): CostItemEditData {
    return { itemName: '', category: '', unitCost: 0 };
  }
}
